// Presentation of leaderboard values. Every "-" on the screen comes from here, so the
// difference between "not measured" and "zero" is decided once.

/** A score, or "-" when there is none. Never 0 for a missing value. */
export function fmtScore(value: number | null | undefined, decimals = 3): string {
  return typeof value === "number" && Number.isFinite(value) ? value.toFixed(decimals) : "-";
}

export function fmtTao(value: number | null | undefined, decimals = 4): string {
  return typeof value === "number" && Number.isFinite(value) ? value.toFixed(decimals) : "-";
}

/** Fixed locale and UTC, so the server and the browser render the same string. */
export function fmtSubmit(iso: string | null | undefined): string {
  if (!iso) return "-";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "-";
  return new Date(t).toISOString().slice(0, 16).replace("T", " ");
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
// Spelled out rather than taken from Intl: en-GB now says "Sept", other locales differ,
// and the server and the browser must print the same thing.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function utcParts(t: number): { date: string; time: string } {
  const d = new Date(t);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return { date: `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`, time: `${hh}:${mm}` };
}

/**
 * A phase's window as two short lines: "Mon 22 Sep, 14:30 (UTC)" and
 * "→ Mon 29 Sep, 14:30 (UTC)". An end on the same day keeps only its time ("→ 08:00 (UTC)")
 * - the date would repeat the line above it. The weekday is there because "Mon 29 Sep"
 * answers "is that this weekend". Every line says UTC: a reader in Warsaw or Tokyo sees
 * one time at a time, not a footnote.
 */
export function fmtPhaseRange(startIso: string, endIso: string): [string, string] {
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return ["-", "-"];
  const a = utcParts(start);
  const b = utcParts(end);
  return [
    `${a.date}, ${a.time} (UTC)`,
    a.date === b.date ? `→ ${b.time} (UTC)` : `→ ${b.date}, ${b.time} (UTC)`,
  ];
}

/**
 * 27015 s -> "07:30:15"; past a day the days split off: "5d 06:03:36", not "126:03:36" -
 * an upload window is a week, and nobody reads three-digit hours at a glance. Null when
 * the moment has passed - the caller says so in words.
 */
export function fmtCountdown(msRemaining: number): string | null {
  if (msRemaining <= 0) return null;
  const total = Math.floor(msRemaining / 1000);
  const days = Math.floor(total / 86_400);
  const h = String(Math.floor((total % 86_400) / 3600)).padStart(2, "0");
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return days > 0 ? `${days}d ${h}:${m}:${s}` : `${h}:${m}:${s}`;
}


/**
 * A hotkey shortened from both ends. The tail matters as much as the head - two miners
 * can share a prefix - so this never truncates one side away. `chars` drops to 4 on a
 * phone, where the full 8+8 form would not leave room for the score beside it.
 */
export function shortHotkey(hotkey: string, chars = 8): string {
  return hotkey.length <= chars * 2 + 4 ? hotkey : `${hotkey.slice(0, chars)}…${hotkey.slice(-chars)}`;
}

/**
 * A score's standing, used for colour. Three bands and nothing finer: the table is
 * sorted, so colour only has to say "strong / ordinary / weak" at a glance.
 */
export type ScoreTone = "strong" | "plain" | "weak" | "none";

export function scoreTone(value: number | null | undefined): ScoreTone {
  if (typeof value !== "number" || !Number.isFinite(value)) return "none";
  if (value >= 0.5) return "strong";
  return value >= 0.25 ? "plain" : "weak";
}

/**
 * A stable hue for a hotkey, so the same miner keeps the same dot between visits and
 * between pages. Only a visual handle - nothing reads meaning into the colour.
 */
export function hotkeyHue(hotkey: string): number {
  let hash = 0;
  for (let i = 0; i < hotkey.length; i += 1) hash = (hash * 31 + hotkey.charCodeAt(i)) % 360;
  return hash;
}

/** A duration in the coarsest unit that still says something: "7d", "18h", "45m". */
export function fmtSpan(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "-";
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}
